import { entityPool, Model } from '../../core/engine';
import { makeObservable, observable } from 'mobx';
import { Project, Shot, Space } from '../../app/entities';
import { ManyToOne } from '../../core/engine/decorators';
import { api } from '../../api';
import { catchError } from '../../core/catch-error';

import { PropertyType, type CreatePropertyInput } from '@producer/properties';

export class Property extends Model {
  name: string;

  type: PropertyType;

  spaceId: string;

  @ManyToOne('properties')
  space: Space;

  projectId: string;

  @ManyToOne('properties')
  project: Project;

  target: 'shot' | 'strip';

  key: string;

  isLocked: boolean;

  resourceId?: string;

  resourceType?: 'Tag' | 'Location' | 'Character';

  options: string[] = [];

  ref?: string;

  constructor() {
    super('properties');

    this.options = [];

    makeObservable(this, {
      name: observable,
      type: observable,
      options: observable,
    });
  }

  static async create(input: CreatePropertyInput) {
    try {
      const { data } = await api.post(`/properties`, input);
      const property = Object.assign(new Property(), { ...data });
      entityPool.insert(property);

      /**
       * Push the property in the views to mirror the update on the BE
       */
      const project = Project.getOne(input.projectId);

      if (project) {
        project.views.forEach((view) => view.addProperty(property));
      }

      return data;
    } catch (e) {
      catchError(e);
    }
  }

  override async update(input: Partial<Omit<this, '_id'>>) {
    const currentOptions = JSON.parse(JSON.stringify(this.options || []));
    await super.update(input);

    if (
      [PropertyType.SingleSelect, PropertyType.MultiSelect].includes(this.type) &&
      input.options
    ) {
      const isUpdateOrDeleteOperation = input.options.length <= (currentOptions?.length || 0);

      if (isUpdateOrDeleteOperation) {
        const { newValue, oldValue } = this.findChangedOption(currentOptions, input.options);

        Shot.getAll().forEach((el) => {
          if (this._id in el.properties) {
            if (this.type === PropertyType.SingleSelect && el.properties[this._id] === oldValue) {
              el.properties[this._id] = !!newValue ? newValue : null;
            }

            if (
              this.type === PropertyType.MultiSelect &&
              el.properties[this._id].includes(oldValue)
            ) {
              (el.properties[this._id] as string[]).splice(
                (el.properties[this._id] as string[]).indexOf(oldValue),
                1,
                newValue,
              );
            }
          }
        });
      }
    }

    for (const key in input) {
      this[key] = input[key];
    }
  }

  private findChangedOption(oldArray: string[], newArray: string[]) {
    const changes = { oldValue: null, newValue: null };

    for (const el of newArray) {
      if (!oldArray.includes(el)) {
        changes.newValue = el;
      }
    }

    for (const el of oldArray) {
      if (!newArray.includes(el)) {
        changes.oldValue = el;
      }
    }

    return changes;
  }

  toPOJO(): Record<string, any> {
    return {
      id: this._id,
      name: this.name,
      type: this.type,
    };
  }
}
